import React from 'react';
import { ExternalLink, RefreshCw, Zap, Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardShell from '../components/DashboardShell';
import { Button } from '../components/ui/button';

// The Snagcart Site Architect (autonomous builder) is deployed as its own
// service. The URL is configurable so non-prod environments can point at a
// different instance.
const AI_ENGINEER_URL =
  (process.env.REACT_APP_AI_ENGINEER_URL && process.env.REACT_APP_AI_ENGINEER_URL !== '')
    ? process.env.REACT_APP_AI_ENGINEER_URL
    : 'https://bolt-diy-production-0aab.up.railway.app';

const LOAD_TIMEOUT_MS = 40000;

// Animated pulse icon shown while the engine boots
function PulseIcon() {
  return (
    <>
      <style>{`
        @keyframes snag-pulse {
          0%   { transform: scale(1);   opacity: 1; }
          50%  { transform: scale(1.1); opacity: 0.8; }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes snag-ring {
          0%   { transform: scale(0.8); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .snag-pulse { animation: snag-pulse 1.5s ease-in-out infinite; }
        .snag-ring  { animation: snag-ring 1.5s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .snag-pulse, .snag-ring { animation: none; }
        }
      `}</style>
      <div className="relative flex items-center justify-center">
        <span className="snag-ring absolute w-14 h-14 rounded-full border-2 border-emerald-400/40" />
        <div
          className="snag-pulse w-12 h-12 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20"
          style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
        >
          <Zap className="w-6 h-6 text-white" />
        </div>
      </div>
    </>
  );
}

/**
 * AIEngineer — Snagcart Site Architect
 *
 * An autonomous AI-powered website builder that lets users describe any
 * website or web application in natural language and generates the complete
 * codebase. Websites created here appear under "My Websites" on the dashboard.
 *
 * The builder runs as a separate service for performance isolation. This page
 * acts as a branded launcher that redirects the user to the builder interface.
 */
function AIEngineerInner({ user }) {
  const navigate = useNavigate();
  const src = React.useMemo(() => AI_ENGINEER_URL, []);

  // The builder requires cross-origin isolation (COOP/COEP) for WebContainers,
  // so we redirect the whole tab instead of embedding in an iframe.
  const launch = React.useCallback(() => {
    window.location.assign(src);
  }, [src]);

  React.useEffect(() => {
    const t = setTimeout(launch, 800);
    return () => clearTimeout(t);
  }, [launch]);

  return (
    <DashboardShell
      user={user}
      title="Site Architect"
      subtitle="Describe your vision — our AI builds the complete website for you."
      activeNav="ai-engineer"
    >
      <div
        className="relative flex w-full flex-col items-center justify-center gap-6 bg-[#080808]"
        style={{ height: 'calc(100vh - 56px)' }}
        data-testid="ai-engineer-launch"
      >
        {/* Back to Dashboard button - always visible */}
        <button
          onClick={() => navigate('/dashboard')}
          className="absolute top-5 left-5 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] border border-white/[0.08] hover:bg-white/[0.1] hover:border-white/[0.15] text-zinc-400 hover:text-white text-sm transition-all"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <PulseIcon />

        <div className="text-center space-y-2 max-w-md">
          <h2 className="text-lg font-semibold text-white">Launching Site Architect</h2>
          <p className="text-sm text-zinc-400">
            Your AI-powered workspace is starting up. Describe any website idea and watch it come to life.
          </p>
        </div>

        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Connecting to builder...
        </div>

        <Button
          size="sm"
          onClick={launch}
          data-testid="ai-engineer-launch-now"
          className="h-9 gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open Site Architect
        </Button>

        <p className="max-w-sm text-center text-[11px] text-zinc-600">
          Websites you create will appear under My Websites on your dashboard.
        </p>
      </div>
    </DashboardShell>
  );
}

/**
 * Error boundary so an unexpected render fault degrades gracefully.
 */
class AIEngineer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[AIEngineer] render error', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <DashboardShell
          user={this.props.user}
          title="Site Architect"
          subtitle="Describe any website — Snagcart's AI builds, previews, and deploys it for you."
          activeNav="ai-engineer"
        >
          <div className="flex h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center" data-testid="ai-engineer-error">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-red-500/15 border border-red-500/30">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <div className="max-w-md space-y-1">
              <p className="text-sm font-medium text-zinc-200">Could not load the Site Architect</p>
              <p className="text-xs text-zinc-500">Please reload the page. If this keeps happening, open the builder in a new tab.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => window.location.reload()}
                className="h-8 gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Reload
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(AI_ENGINEER_URL, '_blank', 'noopener,noreferrer')}
                className="h-8 gap-1.5 border-white/[0.08] bg-white/[0.03] text-zinc-300 hover:text-white"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open in new tab
              </Button>
            </div>
          </div>
        </DashboardShell>
      );
    }

    return <AIEngineerInner user={this.props.user} />;
  }
}

export default AIEngineer;
